import {inject, Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {AccountService, DataStateService, StorageService} from '../../service';
import {NAVBAR_ITEMS} from '../../../shared/constants/app-constants';
import {TranslateService} from '@ngx-translate/core';
import {NavbarItemModel, NavbarStorageData} from './navbar-item.model';
import {AppKleros, FunzioniKleros, TipoProfilo} from '../../../shared/models';
import {Router} from '@angular/router';
import {map} from 'rxjs/operators';
import {AppConfig} from '../../../shared/constants/mappa-apps';

@Injectable({providedIn: 'root'})
export class NavbarService {

	private readonly vociNavbarBS: BehaviorSubject<NavbarStorageData> = new BehaviorSubject({});
	private router = inject(Router);

	constructor(private storage: StorageService,
	            private account: AccountService,
	            private state: DataStateService,
	            private translate: TranslateService) {
		if (this.storage.hasItemSession(NAVBAR_ITEMS)) {
			// auto-carico le voci navbar
			this.vociNavbarBS.next(JSON.parse(this.storage.getItemSession(NAVBAR_ITEMS)));
		}
		this.translate.onLangChange.subscribe(() => {
			const activeApp = this.state.currentActiveAppId;
			this.saveNavbarItems(activeApp, this.vociNavbarBS.getValue()[activeApp]);
		});
	}

	/**
	 * Gestione caricamento voci funzionalità associate ad un'app.
	 * - passare stopAutoRouting = true se si vuole indirizzare manualmente in un'altra pagina che non sia il grafo famiglia
	 */
	loadNavItems(app: AppConfig, stopAutoRouting = false) {

		this.state.setOpenAppConfig(app); // settiamo i dati app -> funzioni nello state x sicurezza

		const isAdminOrConsulente = this.account.hasAnyProfilo([TipoProfilo.CONSULENTE, TipoProfilo.ADMIN]);
		const idApplicazione: number = app.id;
		const urlApp: string = app.nome.toLowerCase();

		const navbarItems: NavbarItemModel[] = app.funzioni.map(f => {
			// le funzioni saranno già "scremate" per quelle attive dell'utente e nell'ordine corretto (campo "ordine")
			switch (f.nome) {
				case FunzioniKleros.FAMIGLIA:
				case FunzioniKleros.FAMIGLIA_DOSSIER:
					return {
						funzione: f.nome,
						icon: 'mdi mdi-human-male-female-child',
						labelTranslateKey: 'famiglia',
						routerLink: 'applicazioni/' + urlApp + '/funzionalita/famiglia'
					};
				case FunzioniKleros.PATRIMONIO:
				case FunzioniKleros.PATRIMONIO_DOSSIER:
					return {
						funzione: f.nome,
						icon: 'mdi mdi-bank',
						labelTranslateKey: 'patrimonio',
						routerLink: 'applicazioni/' + urlApp + '/funzionalita/patrimonio'
					};
				case FunzioniKleros.ALTRE_INFORMAZIONI:
				case FunzioniKleros.ALTRE_INFORMAZIONI_DOSSIER:
					return {
						funzione: f.nome,
						icon: 'mdi mdi-text-box-search-outline',
						labelTranslateKey: 'altre_informazioni',
						routerLink: 'applicazioni/' + urlApp + '/funzionalita/altre-informazioni'
					};
				case FunzioniKleros.REPORT:
				case FunzioniKleros.REPORT_DOSSIER:
					return {
						funzione: f.nome,
						icon: 'mdi mdi-chart-box-multiple-outline',
						labelTranslateKey: 'report',
						routerLink: 'applicazioni/' + urlApp + '/funzionalita/report'
					};
				case FunzioniKleros.ANALISI:
				case FunzioniKleros.ANALISI_DOSSIER:
					return {
						funzione: f.nome,
						icon: 'mdi mdi-chart-pie',
						labelTranslateKey: 'analisi',
						routerLink: 'applicazioni/' + urlApp + '/funzionalita/analisi'
					};
				case FunzioniKleros.REGOLE:
					return {
						funzione: f.nome,
						icon: 'mdi mdi-printer-pos-cog-outline',
						labelTranslateKey: 'regole',
						routerLink: 'applicazioni/' + urlApp + '/funzionalita/regole'
					};
				case FunzioniKleros.KNOW_HOW:
					return {
						funzione: f.nome,
						icon: 'mdi mdi-head-lightbulb-outline',
						labelTranslateKey: 'knowHow',
						routerLink: 'applicazioni/' + urlApp + '/funzionalita/know-how'
					};
				case FunzioniKleros.PARAMETRI_GENERALI:
					return {
						funzione: FunzioniKleros.PARAMETRI_GENERALI,
						icon: 'mdi mdi-tune-variant',
						labelTranslateKey: 'parametriGenerali',
						routerLink: 'applicazioni/' + urlApp + '/funzionalita/parametri-generali'
					};
				// TODO - QUI ALTRE FUNZIONI DA GESTIRE...
				default:
					return null;
			}
		}).filter(navItem => !!navItem);

		// in automatico mi posiziono in pag. inserimento/edit grafo famiglia
		if (!stopAutoRouting && navbarItems?.length > 0) {
			this.router.navigate([navbarItems[0].routerLink], {state: navbarItems[0].state});
		}
		// SE CONSULENTE/ADMIN --> inserisco sempre voce navbar x tornare alla tabella di selezione cliente:
		if (isAdminOrConsulente && [AppKleros.MYARPANONIMO, AppKleros.MYARPADOSSIER].includes(app.nome)) {
			navbarItems.unshift({
				icon: 'mdi mdi-folder-open',
				labelTranslateKey: 'selezione_dossier',
				routerLink: 'applicazioni/' + app.nome.toLowerCase() + '/dossier',
				state: {idApplicazione: idApplicazione, appFunzioni: app},
				parentItem: true,
				alwaysActive: true
			});
		}
		this.saveNavbarItems(urlApp, navbarItems);
	}

	saveNavbarItems(appId: string, navItems: NavbarItemModel[]): void {
		this.translateLabels(navItems);
		const navbarData: NavbarStorageData = this.vociNavbarBS.value;
		navbarData[appId] = navItems; // salvo nav-items dell'app in oggetto "mappa"
		this.vociNavbarBS.next(navbarData); // aggiorno mappa nel BS
		this.storage.setItemSession(NAVBAR_ITEMS, JSON.stringify(navbarData));
	}

	setNavbarItemsFromStorage() {
		this.vociNavbarBS.next(JSON.parse(this.storage.getItemSession(NAVBAR_ITEMS)));
	}

	private translateLabels(items: NavbarItemModel[]) {
		if (items) {
			items.forEach(item => {
				item.label = this.translate.instant(item.labelTranslateKey);
				if (item.children) {
					this.translateLabels(item.children);
				}
			});
		}
	}

	get navbarItems$(): Observable<NavbarItemModel[]> {
		return this.vociNavbarBS.asObservable()
			.pipe(map(navbarData => this.state.currentActiveAppId ? navbarData[this.state.currentActiveAppId] : []));
	}

	get currentNavbarData(): NavbarStorageData {
		return this.vociNavbarBS.getValue();
	}

}
